import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  useEffect(() => {
    document.title = '关于我们 - 九九免费视频';
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('website_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      form.reset();
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d9rdt9me3oq4jqumu070', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(
          Array.from(formData.entries())
            .filter(([key]) => key !== 'website_alt')
            .map(([k, v]) => [k, v as string])
        ).toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      const isSpam = responseText.includes('spam') || responseText.includes('form data is spam');

      if (response.ok && !isSpam && parsed.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.meta?.detail || parsed?.message || '提交失败，请稍后重试';
        setFormError(serverMsg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请稍后再试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <section className="bg-background-100 py-16 md:py-20 px-4 md:px-6">
          <div className="max-w-[1920px] mx-auto text-center">
            <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-900">
              关于九九视频
            </h1>
            <p className="mt-4 text-foreground-500 max-w-2xl mx-auto text-sm md:text-base leading-relaxed">
              我们致力于为用户提供最优质的免费视频观看体验
            </p>
          </div>
        </section>

        <section className="py-14 md:py-20 px-4 md:px-6">
          <div className="max-w-[1920px] mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-16 items-center">
              <div>
                <h2 className="font-heading text-xl md:text-3xl font-bold text-foreground-900 mb-6">
                  品牌介绍
                </h2>
                <div className="space-y-4 text-foreground-600 text-sm md:text-base leading-relaxed">
                  <p>
                    九九免费视频（JiujiuFreeVideo）成立于2024年，是一家专注于为用户提供高清免费视频内容的在线平台。我们秉持"免费、高清、便捷"的理念，汇聚全网优质视频资源，涵盖电影、电视剧、综艺、纪录片、动漫等多种类型。
                  </p>
                  <p>
                    平台无需注册即可免费观看，每日更新最新内容，采用先进的视频播放技术，确保流畅播放零缓冲。无论是热播大片还是经典老片，用户都能够在九九视频找到自己喜爱的内容。
                  </p>
                  <p>
                    我们的团队由热爱视频内容的专业人士组成，持续优化平台体验，不断丰富内容库，力求成为用户休闲娱乐的首选视频平台。
                  </p>
                </div>
              </div>
              <div className="relative rounded-2xl overflow-hidden aspect-[4/3]">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20digital%20entertainment%20workspace%20with%20multiple%20screens%20displaying%20videos%2C%20warm%20cinematic%20lighting%2C%20creative%20office%20atmosphere%2C%20professional%20video%20production%20setup%2C%20warm%20amber%20and%20dark%20tones%2C%20contemporary%20design&width=800&height=600&seq=about-brand-2026&orientation=landscape"
                  alt="九九免费视频品牌形象"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
          <div className="max-w-[1920px] mx-auto">
            <h2 className="font-heading text-xl md:text-3xl font-bold text-foreground-900 text-center mb-10">
              平台信誉
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { icon: 'ri-shield-check-line', title: '安全可靠', desc: '平台严格审核内容，确保所有视频资源安全无病毒，用户可以放心观看。' },
                { icon: 'ri-4k-line', title: '高清画质', desc: '99%以上的视频资源支持高清及以上画质，带来极致的视觉体验。' },
                { icon: 'ri-speed-up-line', title: '流畅播放', desc: '采用CDN加速技术，全国多节点覆盖，确保视频加载迅速，播放无卡顿。' },
                { icon: 'ri-user-heart-line', title: '用户至上', desc: '无需注册即可观看，无广告干扰，全心全意为用户提供最佳观影体验。' },
              ].map((item, idx) => (
                <div key={idx} className="bg-background-50 rounded-xl p-6 text-center border border-background-200/70">
                  <div className="w-14 h-14 mx-auto flex items-center justify-center bg-primary-100 rounded-xl mb-4">
                    <i className={`${item.icon} text-primary-600 text-2xl`}></i>
                  </div>
                  <h3 className="font-heading font-bold text-foreground-900 text-base mb-2">{item.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>

            <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              {[
                { value: '10万+', label: '视频资源' },
                { value: '500万+', label: '日活用户' },
                { value: '99%', label: '用户好评' },
                { value: '24h', label: '内容更新' },
              ].map((stat, idx) => (
                <div key={idx}>
                  <div className="text-3xl md:text-4xl font-label font-bold text-accent-500">{stat.value}</div>
                  <div className="mt-2 text-sm text-foreground-500">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 px-4 md:px-6">
          <div className="max-w-[1920px] mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-16">
              <div>
                <h2 className="font-heading text-xl md:text-3xl font-bold text-foreground-900 mb-6">
                  联系我们
                </h2>
                <div className="space-y-5">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-secondary-100 rounded-lg shrink-0">
                      <i className="ri-mail-line text-secondary-600 text-lg"></i>
                    </div>
                    <div>
                      <h4 className="font-medium text-foreground-900 text-sm">电子邮箱</h4>
                      <p className="text-sm text-foreground-500">contact@jiujiuvideo.com</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-secondary-100 rounded-lg shrink-0">
                      <i className="ri-phone-line text-secondary-600 text-lg"></i>
                    </div>
                    <div>
                      <h4 className="font-medium text-foreground-900 text-sm">客服电话</h4>
                      <p className="text-sm text-foreground-500">400-888-9999</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-secondary-100 rounded-lg shrink-0">
                      <i className="ri-map-pin-line text-secondary-600 text-lg"></i>
                    </div>
                    <div>
                      <h4 className="font-medium text-foreground-900 text-sm">公司地址</h4>
                      <p className="text-sm text-foreground-500">北京市朝阳区建国路88号SOHO现代城A座18层</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-secondary-100 rounded-lg shrink-0">
                      <i className="ri-time-line text-secondary-600 text-lg"></i>
                    </div>
                    <div>
                      <h4 className="font-medium text-foreground-900 text-sm">工作时间</h4>
                      <p className="text-sm text-foreground-500">周一至周五 9:00 - 18:00</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-background-100 rounded-2xl p-6 md:p-8 border border-background-200/70">
                <h3 className="font-heading font-bold text-foreground-900 text-lg mb-6">发送消息</h3>
                <form id="contact-form" data-readdy-form="" onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-foreground-900 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-foreground-900 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">主题</label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-foreground-900 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
                      placeholder="请输入消息主题"
                    />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">消息内容</label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-foreground-900 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all resize-none"
                      placeholder="请输入您的消息（最多500字符）"
                    ></textarea>
                  </div>
                  <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                  <button
                    type="submit"
                    className="w-full py-3 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-sm font-semibold whitespace-nowrap transition-all cursor-pointer"
                  >
                    发送消息
                  </button>

                  {formStatus === 'success' && (
                    <div className="p-3 bg-accent-100 text-accent-800 rounded-lg text-sm text-center">
                      消息已发送成功！我们会尽快回复您。
                    </div>
                  )}
                  {formStatus === 'error' && (
                    <div className="p-3 bg-primary-100 text-primary-700 rounded-lg text-sm text-center">
                      {formError}
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}